// Canvas 2D lip-colour renderer for the photo-based VTO pipeline.
//
// The approach: draw the original snapshot; then composite a coloured lip
// region (outer contour minus inner mouth cavity) using blend modes that
// preserve the underlying lip texture. Finish variants add a second pass
// (specular highlight for gloss, matte mask dampening for matte, etc).
//
// Trade-off vs a shader-based Three.js path: ~30 lines of code instead of
// ~400, runs on every device Canvas 2D supports, quality loss is minimal
// for still images where we can afford a medium-gaussian feather to hide
// landmark jitter.

import { stackBlur } from './stackBlur';
import {
  INNER_LIP_INDICES,
  OUTER_LIP_INDICES,
  SHARP_LIP_LANDMARKS
} from '../tracking/lipIndices';
import type { LipDetection } from '../tracking/faceLandmarker';
import type { LipFinish } from '../catalog/catalog';
import type { SemanticFaceMap } from '../semantic/semanticFaceMap';
import { combineMasks, intersectMasks, scaleMask, shrinkMask } from './maskUtils';

export type { LipFinish };

export interface LipRenderOptions {
  hex: string;        // '#rrggbb'
  finish: LipFinish;
  opacity: number;    // 0..1 — per-shade curated value from shade-colors.json
  innerHoleScale?: number;
  finishStrengthScale?: number;
  semanticMap?: SemanticFaceMap | null;
}

export interface LipRenderResult {
  canvas: HTMLCanvasElement;  // final composited output at source resolution
}

function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace('#', '');
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return [r, g, b];
}

// Rec. 709 relative luminance in 0..1. Used to route very-dark hexes
// away from the multiply path (which turns to mud on dark colours).
function luminance(rgb: [number, number, number]): number {
  return (0.2126 * rgb[0] + 0.7152 * rgb[1] + 0.0722 * rgb[2]) / 255;
}

// Traces a closed contour through the given landmark indices, combining:
//
//   1. Chaikin-style quadratic smoothing between smooth landmarks — soft
//      curves instead of polygonal line segments.
//   2. Sharp `lineTo` at whitelisted landmarks (the lip commissures 61 and
//      291) so the path closes exactly at the real mouth corners instead
//      of cutting inside them via a midpoint curve.
//   3. Optional cupid's-bow dampening: when landmarks 37, 0, 267 are all
//      present we lift landmark 0 a fraction of the way toward the line
//      connecting 37 and 267, softening the central V-dip without losing it.
//   4. Optional inward erosion toward the polygon centroid. MediaPipe's
//      outer-lip landmarks sit marginally outside the vermilion border; a
//      small pull (~1.5px) compensates without starving the edge of colour.
function tracePolygon(
  ctx: CanvasRenderingContext2D,
  detection: LipDetection,
  indices: readonly number[],
  erodePixels: number = 0,
  cupidsBowLift: number = 0,
  outwardPushSharp: number = 0,
  // Face-frame "up" in canvas coordinates. At roll=0 this is (0, -1), i.e.
  // straight up in image space — matches pre-v0.3.0 behaviour exactly. At
  // non-zero roll the sharp-corner tilt follows the tilted face instead of
  // pointing to the camera's up.
  faceUpX: number = 0,
  faceUpY: number = -1
): void {
  const { landmarks, imageWidth: W, imageHeight: H } = detection;

  type Pt = { x: number; y: number; sharp: boolean };
  const pts: Pt[] = [];
  let idxLeftPeak = -1;
  let idxCentralDip = -1;
  let idxRightPeak = -1;
  for (let i = 0; i < indices.length; i++) {
    const idx = indices[i];
    if (idx === undefined) continue;
    const p = landmarks[idx];
    if (!p) continue;
    const arrayIndex = pts.length;
    pts.push({ x: p.x * W, y: p.y * H, sharp: SHARP_LIP_LANDMARKS.has(idx) });
    if (idx === 37) idxLeftPeak = arrayIndex;
    else if (idx === 0) idxCentralDip = arrayIndex;
    else if (idx === 267) idxRightPeak = arrayIndex;
  }
  const n = pts.length;
  if (n < 3) return;

  // Cupid's-bow adjustment: landmark 0 is the central dip; 37/267 are the
  // peaks. We keep pts[centralDip] at its original y (so the adjacent
  // midpoints mid(37,0) and mid(0,267) are untouched) and instead compute
  // a separate "pushed" y that will be used ONLY as the control point of
  // the quadratic drawn at i=centralDip. This isolates the depth change
  // to the dip itself and avoids dragging the surrounding curve down,
  // which would expose the underlying lip colour on either side.
  //
  // With this scheme the visible apex depth is
  //   apex_depth = (0.75 + 0.5 * |lift|) * native_depth
  // so a lift of -1.05 yields ~1.275 × native depth.
  let pushedCenterY: number | null = null;
  if (
    cupidsBowLift !== 0 &&
    idxLeftPeak >= 0 &&
    idxCentralDip >= 0 &&
    idxRightPeak >= 0
  ) {
    const left = pts[idxLeftPeak]!;
    const right = pts[idxRightPeak]!;
    const center = pts[idxCentralDip]!;
    const targetY = (left.y + right.y) / 2;
    pushedCenterY = center.y + (targetY - center.y) * cupidsBowLift;
  }

  if (erodePixels !== 0 || outwardPushSharp > 0) {
    let cx = 0;
    let cy = 0;
    for (const p of pts) {
      cx += p.x;
      cy += p.y;
    }
    cx /= n;
    cy /= n;
    for (let i = 0; i < n; i++) {
      const p = pts[i]!;
      if (p.sharp) {
        // Sharp landmarks (mouth commissures) are exempt from centroid
        // erosion. When outwardPushSharp > 0 we extend them along a
        // tangent-aware outward normal plus a fixed upward tilt in canvas
        // coordinates. On a horizontally-oriented lip, the tangent normal
        // at a corner points almost purely east/west; real lip corners
        // close diagonally up-and-out, so we add a vertical bias of -1
        // (pre-normalisation) to steer the push toward ~1:30 on the right
        // commissure and ~10:30 on the left — a clean NE/NW wedge.
        if (outwardPushSharp <= 0) continue;
        const prev = pts[(i + n - 1) % n]!;
        const next = pts[(i + 1) % n]!;
        const d1x = p.x - prev.x;
        const d1y = p.y - prev.y;
        const l1 = Math.hypot(d1x, d1y);
        const d2x = next.x - p.x;
        const d2y = next.y - p.y;
        const l2 = Math.hypot(d2x, d2y);
        if (l1 < 0.01 || l2 < 0.01) continue;
        // Outward normal of an edge (dx, dy) for our CW winding is (dy, -dx).
        // The tilt bias is applied along face-up (faceUpX, faceUpY) rather
        // than canvas-up, so on a head rolled 20° the commissure extension
        // still points to the corners of the mouth's natural NE/NW wedge
        // instead of lagging toward the camera's up axis.
        const UPWARD_TILT = 1.0;
        const nx = d1y / l1 + d2y / l2 + faceUpX * UPWARD_TILT;
        const ny = -d1x / l1 + -d2x / l2 + faceUpY * UPWARD_TILT;
        const nl = Math.hypot(nx, ny);
        if (nl < 0.01) continue;
        p.x += (nx / nl) * outwardPushSharp;
        p.y += (ny / nl) * outwardPushSharp;
        continue;
      }
      if (erodePixels === 0) continue;
      const dx = cx - p.x;
      const dy = cy - p.y;
      const d = Math.hypot(dx, dy);
      if (d < 1) continue;
      p.x += (dx / d) * erodePixels;
      p.y += (dy / d) * erodePixels;
    }
  }

  // Always start at the midpoint of the last→first segment — the loop below
  // handles both sharp and smooth cases for every landmark, and starting at
  // a midpoint closes the contour cleanly whether pts[0] is sharp or not.
  const last = pts[n - 1]!;
  const first = pts[0]!;
  ctx.moveTo((last.x + first.x) / 2, (last.y + first.y) / 2);

  for (let i = 0; i < n; i++) {
    const curr = pts[i]!;
    const next = pts[(i + 1) % n]!;
    if (curr.sharp) {
      // Arrive at the exact landmark; the next iteration's quadratic (or
      // lineTo) will continue from here, giving a true corner in the path.
      ctx.lineTo(curr.x, curr.y);
    } else {
      const midX = (curr.x + next.x) / 2;
      const midY = (curr.y + next.y) / 2;
      // At the central cupid's-bow dip, displace the control point's y
      // without changing the landmark's native position used for midpoints.
      const ctrlY =
        i === idxCentralDip && pushedCenterY !== null ? pushedCenterY : curr.y;
      ctx.quadraticCurveTo(curr.x, ctrlY, midX, midY);
    }
  }
  ctx.closePath();
}

// Builds the lip-shaped mask on its own canvas so we can blur the edges
// (hides landmark jitter) and composite with blend modes independently.
function buildLipMask(
  detection: LipDetection,
  options: {
    innerHoleScale?: number;
    featherScale?: number;
    outerCornerScale?: number;
  } = {}
): HTMLCanvasElement {
  const W = detection.imageWidth;
  const H = detection.imageHeight;
  const mask = document.createElement('canvas');
  mask.width = W;
  mask.height = H;
  const mctx = mask.getContext('2d', { willReadFrequently: true });
  if (!mctx) return mask;

  // Feather scales with image size: ~0.2% of the shorter edge is a tight
  // softening that hides landmark jitter without bleeding onto the skin
  // outside the natural lip contour. Values above ~0.3% read as "sloppy
  // lipstick application" on the composited result.
  const feather = Math.max(1.0, Math.min(W, H) * 0.002 * (options.featherScale ?? 1));

  // Outer polygon gets a light inward erosion so the painted region sits
  // just inside MediaPipe's (slightly outward-biased) vermilion-border
  // landmarks. Tuned so the feathered edge still reaches the real lip edge
  // — more aggressive erosion exposes the original lip colour at the rim.
  // Inner polygon is left untouched so the mouth-opening hole stays where
  // the landmarks put it.
  const erodeOuter = Math.max(0.8, Math.min(W, H) * 0.0012);
  // Cupid's-bow lift: negative value pushes the control point BELOW the
  // MediaPipe landmark so the rendered curve apex goes deeper than the raw
  // landmark. Decoupled from the adjacent midpoints (see tracePolygon) so
  // the surrounding curve is NOT dragged down. The apex depth formula is
  //   apex_depth = (0.75 + 0.5 * |lift|) * native_depth
  // We target ~1.275 × native depth (the depth the user calibrated as
  // "perfect" in the previous scheme), which corresponds to lift = -1.05.
  const cupidsBowLift = -1.05;

  // Outer commissures get a ~4.8-px push along a tangent-aware normal
  // combined with an upward tilt. MediaPipe's 291/61 sits at the inner
  // side of the commissure; this extends the rendered corner to the
  // natural NE/NW wedge that real lips close with (~1:30 on the right,
  // ~10:30 on the left).
  const outerCornerPush = Math.max(2.5, Math.min(W, H) * 0.004 * (options.outerCornerScale ?? 1));
  const innerHoleScale = options.innerHoleScale ?? 1;
  const innerExpand = innerHoleScale > 1
    ? -Math.max(0.4, Math.min(W, H) * 0.0009 * (innerHoleScale - 1) * 6)
    : 0;

  // Face-frame "up" in canvas coordinates, derived from detection.pose.roll.
  // At roll=0 this resolves to (0, -1) — exactly the pre-v0.3.0 constant.
  const roll = detection.pose.roll;
  const faceUpX = Math.sin(roll);
  const faceUpY = -Math.cos(roll);

  // Even-odd fill: outer path - inner path = lip annulus only.
  mctx.beginPath();
  tracePolygon(
    mctx,
    detection,
    OUTER_LIP_INDICES,
    erodeOuter,
    cupidsBowLift,
    outerCornerPush,
    faceUpX,
    faceUpY
  );
  // Inner ring: outwardPushSharp=0 quindi faceUpX/Y oggi non hanno effetto,
  // ma li passiamo comunque per non lasciare la chiamata a 6 args contro
  // una firma a 8 — se in futuro qualcuno passa un push > 0 la direzione
  // resta consistente con l'outer ring invece di cadere sul default canvas-up.
  tracePolygon(mctx, detection, INNER_LIP_INDICES, innerExpand, 0, 0, faceUpX, faceUpY);
  mctx.fillStyle = '#ffffff';
  mctx.fill('evenodd');

  stackBlur(mctx, 0, 0, W, H, feather);

  return mask;
}

function refineLipMaskWithSemanticMatte(
  mask: HTMLCanvasElement,
  semanticMap: SemanticFaceMap | null | undefined,
  W: number,
  H: number,
  coreErodePx: number
): HTMLCanvasElement {
  if (!semanticMap) return mask;
  if (semanticMap.source !== 'face-parsing') return mask;
  // The semantic lip mask refines the OUTER edge, but on hard faces (a beard
  // occluding a mouth corner, low contrast) the ONNX parser under-segments and
  // leaves an interior HOLE — a plain intersection would punch that hole into
  // the lip fill (the user's lower-lip corner went uncoloured). Union the
  // semantic mask with an eroded landmark "core" before clipping: the core
  // guarantees the lip interior is always filled while the semantic mask still
  // trims the boundary. No-op on clean faces — the core sits well inside a
  // properly-segmented semantic mask, so it adds nothing there.
  const semantic = scaleMask(semanticMap.masks.lips, W, H);
  const core = shrinkMask(mask, coreErodePx);
  return intersectMasks(mask, [combineMasks(W, H, [semantic, core])]);
}

// Core-erosion radius for the hole-fill above, sized from the lip height so it
// scales with face size / resolution. ~18% of lip height keeps the core safely
// interior (won't re-introduce edge over-reach on clean faces).
function lipMaskCoreErodePx(detection: LipDetection): number {
  const { landmarks, imageHeight: H } = detection;
  let minY = Infinity;
  let maxY = -Infinity;
  for (const idx of OUTER_LIP_INDICES) {
    const p = landmarks[idx];
    if (!p) continue;
    const y = p.y * H;
    if (y < minY) minY = y;
    if (y > maxY) maxY = y;
  }
  const lipHeight = maxY - minY;
  if (!Number.isFinite(lipHeight) || lipHeight <= 0) return 8;
  return Math.max(4, Math.min(24, lipHeight * 0.18));
}

function semanticLipBorderMask(
  semanticMap: SemanticFaceMap | null | undefined,
  W: number,
  H: number
): HTMLCanvasElement | null {
  if (!semanticMap) return null;
  return scaleMask(semanticMap.masks.lipBorder, W, H);
}

// Lightens the given hex by the given amount (0..1) for specular highlights.
function lighten(rgb: [number, number, number], amount: number): [number, number, number] {
  return [
    Math.min(255, Math.round(rgb[0] + (255 - rgb[0]) * amount)),
    Math.min(255, Math.round(rgb[1] + (255 - rgb[1]) * amount)),
    Math.min(255, Math.round(rgb[2] + (255 - rgb[2]) * amount))
  ];
}

// Extracts the specular highlights from the original lip region so we can
// re-composite them on top of the colour pass in `screen` blend mode. Without
// this step the colour fill kills the natural lip sheen and the result looks
// painted. The threshold is tuned so only true highlights (above the mean
// lip luminance) survive; mid-tones go to black and therefore contribute
// nothing under `screen` blend.
function extractLipHighlights(
  source: HTMLCanvasElement | ImageBitmap,
  mask: HTMLCanvasElement,
  W: number,
  H: number,
  threshold: number
): HTMLCanvasElement {
  const c = document.createElement('canvas');
  c.width = W;
  c.height = H;
  const cx = c.getContext('2d', { willReadFrequently: true });
  if (!cx) return c;

  cx.drawImage(source, 0, 0, W, H);
  cx.globalCompositeOperation = 'destination-in';
  cx.drawImage(mask, 0, 0);
  cx.globalCompositeOperation = 'source-over';

  const img = cx.getImageData(0, 0, W, H);
  const data = img.data;
  const range = 1 - threshold;
  for (let i = 0; i < data.length; i += 4) {
    const a = data[i + 3];
    if (a === 0) continue;
    const r = data[i] ?? 0;
    const g = data[i + 1] ?? 0;
    const b = data[i + 2] ?? 0;
    const lum = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
    if (lum < threshold) {
      data[i] = 0;
      data[i + 1] = 0;
      data[i + 2] = 0;
    } else {
      const k = Math.round(255 * ((lum - threshold) / range));
      data[i] = k;
      data[i + 1] = k;
      data[i + 2] = k;
    }
  }
  cx.putImageData(img, 0, 0);
  return c;
}

function highlightStrengthFor(
  finish: LipFinish,
  highCoverage: boolean
): number {
  // v0.3.0 rebalance: dialled down across the board because the new
  // luminosity shape-restoration pass already brings the bright highlights
  // back via the original's luminance channel. Too much screen-blend on
  // top of that tips into "wet plastic" territory.
  if (highCoverage) return 0.15;
  if (finish === 'matte') return 0.12;
  if (finish === 'satin') return 0.32;
  if (finish === 'glossy') return 0.55;
  return 0.40; // metallic
}

// How much of the original lip's luminance pattern to re-imprint on top of
// the colour pass. The luminosity blend preserves the colour layer's hue
// and chroma but replaces its luminance with the source's, so shadows (the
// closing line, the lip's roundness shadow) and highlights both return
// without the heavy flattening we were seeing on dark/matte shades.
// Tuned per finish: stronger on matte and high-coverage where the colour
// pass crushes the most shape, lighter on glossy/metallic where we want
// the finish-specific passes to dominate.
function shapeStrengthFor(
  finish: LipFinish,
  highCoverage: boolean
): number {
  // v0.3.2: highCoverage was 0.65 in v0.3.0 — too much, pulled dark shades
  // like dark-gothic (#2a1620, lum 0.10) toward skin-grey because the
  // luminosity blend rewrites the lip's luminance using the underlying
  // photo (skin lum ~0.55). At 0.40 the closing line + rim shadow still
  // re-imprint but the plum/black character of the colour survives.
  if (highCoverage) return 0.40;
  if (finish === 'matte') return 0.55;
  if (finish === 'satin') return 0.45;
  if (finish === 'glossy') return 0.32;
  return 0.35; // metallic
}

// Produce a masked copy of the original image, cropped to the lip region,
// ready to be composited with a blend mode like 'luminosity' or 'multiply'.
function maskedOriginal(
  source: HTMLCanvasElement | ImageBitmap,
  mask: HTMLCanvasElement,
  W: number,
  H: number
): HTMLCanvasElement {
  const c = document.createElement('canvas');
  c.width = W;
  c.height = H;
  const cx = c.getContext('2d');
  if (!cx) return c;
  cx.drawImage(source, 0, 0, W, H);
  cx.globalCompositeOperation = 'destination-in';
  cx.drawImage(mask, 0, 0);
  return c;
}

export function renderLipOverlay(
  source: HTMLCanvasElement | ImageBitmap,
  detection: LipDetection,
  options: LipRenderOptions
): LipRenderResult {
  const W = detection.imageWidth;
  const H = detection.imageHeight;
  const out = document.createElement('canvas');
  out.width = W;
  out.height = H;
  const ctx = out.getContext('2d');
  if (!ctx) throw new Error('2D context unavailable');

  // Layer 0: original snapshot.
  ctx.drawImage(source, 0, 0, W, H);

  const finishStrengthScale = options.finishStrengthScale ?? 1;
  const landmarkMask = buildLipMask(detection, {
    innerHoleScale: options.innerHoleScale ?? 1,
    featherScale: finishStrengthScale < 0.9 ? 0.94 : 1,
    outerCornerScale: finishStrengthScale < 0.9 ? 0.96 : 1
  });
  const mask = refineLipMaskWithSemanticMatte(
    landmarkMask, options.semanticMap, W, H, lipMaskCoreErodePx(detection)
  );
  const rgb = hexToRgb(options.hex);
  const [r, g, b] = rgb;

  // High-coverage path: very-dark lipsticks (black, deep plum) are fully
  // pigmented in reality — they *cover* the lip rather than tint it. The
  // multiply blend we use for lighter colours produces a muddy brown on
  // dark input pixels and robs the look of its defining crispness. When
  // the hex luminance is below 0.20 we treat the colour as high-coverage
  // and draw it normally (source-over) with the configured opacity.
  const highCoverage = luminance(rgb) < 0.2;

  // Cap source-over alpha on the high-coverage path so extremely opaque
  // dark shades (catalog opacities up to 0.92) don't paint the lip flat
  // and steal the whole scene — cheek/eye layers become impossible to
  // notice next to a fully-painted lip. 0.78 preserves the shade's
  // character while leaving enough of the underlying lip texture to read
  // as "lipstick on a face" rather than "a flat dark rectangle".
  const effectiveOpacity = highCoverage
    ? Math.min(options.opacity, 0.78)
    : options.opacity;

  // Layer 1: colour pass. Multiply tints the lip while preserving the
  // natural lip texture/shadows from the snapshot. Opacity is per-shade.
  const colorLayer = document.createElement('canvas');
  colorLayer.width = W;
  colorLayer.height = H;
  const cctx = colorLayer.getContext('2d');
  if (cctx) {
    cctx.fillStyle = `rgb(${r}, ${g}, ${b})`;
    cctx.fillRect(0, 0, W, H);
    // Clip to the feathered mask.
    cctx.globalCompositeOperation = 'destination-in';
    cctx.drawImage(mask, 0, 0);
  }

  ctx.save();
  ctx.globalAlpha = effectiveOpacity;
  ctx.globalCompositeOperation = highCoverage ? 'source-over' : 'multiply';
  ctx.drawImage(colorLayer, 0, 0);
  ctx.restore();

  const lipBorder = semanticLipBorderMask(options.semanticMap, W, H);
  if (lipBorder) {
    const edgeLayer = document.createElement('canvas');
    edgeLayer.width = W;
    edgeLayer.height = H;
    const ectx = edgeLayer.getContext('2d');
    if (ectx) {
      ectx.fillStyle = `rgb(${r}, ${g}, ${b})`;
      ectx.fillRect(0, 0, W, H);
      ectx.globalCompositeOperation = 'destination-in';
      ectx.drawImage(lipBorder, 0, 0);
      ctx.save();
      ctx.globalAlpha = (highCoverage ? 0.08 : 0.12) * finishStrengthScale;
      ctx.globalCompositeOperation = highCoverage ? 'source-over' : 'color';
      ctx.drawImage(edgeLayer, 0, 0);
      ctx.restore();
    }
  }

  // Layer 1.25: shape restoration. The colour pass above crushes the lip's
  // natural luminance pattern — both the deep shadow of the closing line
  // (where upper and lower lip meet) and the soft shadow-from-shading that
  // gives the lip its round shape. We composite a masked copy of the
  // ORIGINAL in 'luminosity' blend so the colour's hue+chroma survives
  // but the photo's luminance pattern is re-imprinted. Preserves roundness
  // and the closing line at any lip opacity.
  const lipOriginal = maskedOriginal(source, mask, W, H);
  const shapeStrength = shapeStrengthFor(options.finish, highCoverage);
  if (shapeStrength > 0) {
    ctx.save();
    ctx.globalAlpha = shapeStrength;
    ctx.globalCompositeOperation = 'luminosity';
    ctx.drawImage(lipOriginal, 0, 0);
    ctx.restore();
  }

  // Layer 1.5: highlight top-up. Luminosity above already re-imprints the
  // full luminance range, but a light screen-blend of the brightest pixels
  // adds a little extra sparkle on satin/glossy/metallic so the finish
  // reads as the specific material it's meant to be.
  const highlightMap = extractLipHighlights(source, mask, W, H, 0.55);
  const highlightStrength = highlightStrengthFor(options.finish, highCoverage) * finishStrengthScale;
  if (highlightStrength > 0) {
    ctx.save();
    ctx.globalAlpha = highlightStrength;
    ctx.globalCompositeOperation = 'screen';
    ctx.drawImage(highlightMap, 0, 0);
    ctx.restore();
  }

  // Layer 2: finish-specific pass. Skipped entirely for high-coverage dark
  // shades — the normal-blend pass above already covers the lip, so the
  // matte/satin/glossy secondary overlays would only add artefacts.
  if (highCoverage) {
    return { canvas: out };
  }

  if (options.finish === 'matte') {
    // Slight desaturation/dampening of any remaining gloss from the photo —
    // draw the colour a second time with low alpha in 'color' mode to flatten.
    ctx.save();
    ctx.globalAlpha = 0.25 * finishStrengthScale;
    ctx.globalCompositeOperation = 'color';
    ctx.drawImage(colorLayer, 0, 0);
    ctx.restore();
  } else if (options.finish === 'satin') {
    // Single gentle sheen along the upper lip — keeps the colour readable
    // without looking wet.
    ctx.save();
    ctx.globalAlpha = 0.12 * finishStrengthScale;
    ctx.globalCompositeOperation = 'screen';
    ctx.drawImage(colorLayer, 0, 0);
    ctx.restore();
  } else if (options.finish === 'glossy') {
    // v0.4.5: the explicit gloss spot used to sit on the mouth slit
    // (landmarks 13+14 are both inner lip margins) with a 27-px radius
    // and near-white tint — on bright shades like forever-young (#f4a5a0)
    // it read as a white "bollino" straddling the slit and bleeding
    // visibly onto the upper lip. Three fixes:
    //   - Relocate to the middle of the lower lip BODY: landmark 17 is
    //     the outer lower-lip midpoint (bottom edge), so the midpoint
    //     between 14 (inner top) and 17 (outer bottom) is the centre of
    //     the lower lip flesh where real gloss catches light.
    //   - Tighter radius (1.2% vs 1.8% of short edge) so the spot reads
    //     as a specular catch, not a sticker.
    //   - Lighten 0.55 (vs 0.75) so the highlight is a bright-tinted
    //     version of the shade, not nearly-white cream.
    //   - Lower alpha 0.22 (vs 0.35) — shape restoration (luminosity)
    //     already brings the original lip's highlights back; this is
    //     the extra specular on top, not the primary source.
    const highlight = lighten([r, g, b], 0.55);
    ctx.save();
    ctx.globalAlpha = 0.22 * finishStrengthScale;
    ctx.globalCompositeOperation = 'screen';

    const innerLowerLip = detection.landmarks[14];
    const outerLowerLip = detection.landmarks[17];
    if (innerLowerLip && outerLowerLip) {
      const cx = (innerLowerLip.x + outerLowerLip.x) * 0.5 * W;
      const cy = (innerLowerLip.y + outerLowerLip.y) * 0.5 * H;
      const radius = Math.min(W, H) * 0.012 * Math.max(0.82, finishStrengthScale);
      const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, radius);
      grad.addColorStop(0, `rgba(${highlight[0]}, ${highlight[1]}, ${highlight[2]}, 1)`);
      grad.addColorStop(1, `rgba(${highlight[0]}, ${highlight[1]}, ${highlight[2]}, 0)`);
      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.arc(cx, cy, radius, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();
  } else if (options.finish === 'metallic') {
    // Bright highlight band along the upper lip ridge to fake shimmer.
    const shimmer = lighten([r, g, b], 0.85);
    const shimmerLayer = document.createElement('canvas');
    shimmerLayer.width = W;
    shimmerLayer.height = H;
    const sctx = shimmerLayer.getContext('2d');
    if (sctx) {
      sctx.fillStyle = `rgb(${shimmer[0]}, ${shimmer[1]}, ${shimmer[2]})`;
      sctx.fillRect(0, 0, W, H);
      sctx.globalCompositeOperation = 'destination-in';
      sctx.drawImage(mask, 0, 0);
    }
    ctx.save();
    ctx.globalAlpha = 0.22 * finishStrengthScale;
    ctx.globalCompositeOperation = 'screen';
    ctx.drawImage(shimmerLayer, 0, 0);
    ctx.restore();
  }

  return { canvas: out };
}
